import JobSchema, { JobSchemaInputs, JobSchemaOutcome, LedgerDeclaration } from '../../models/job-schema.js';

// A job schema as app-config/app_data/job-schema holds it: what the server's jobSchema registry checks
export interface JobSchemaDefinition {
  name: string;
  version: string;
  description: string;
  inputs: Record<string, { type: string; optional?: boolean; ref?: string }>;
  // serverOnly: the outcome is reached only by a lambda after its own checks; job/complete refuses it to a person
  outcomes: Record<
    string,
    { inputs?: Record<string, { type: string; optional?: boolean; ref?: string }>; serverOnly?: boolean }
  >;
  jobMetadata?: string[];
  workflowMetadata?: string[];
  publicLedger?: LedgerDeclaration[];
  authorable?: boolean;
  // A generic job: what its node's CONFIG holds and what each outcome's ANSWERS holds (builder/generic/describe.ts)
  config?: Record<string, ConfigProp>;
  answers?: Record<string, Record<string, unknown>>;
}

// One property of a generic job's config description, as app-config install/job-descriptions.js checks it
export interface ConfigProp {
  // text, longText, number, boolean, choice, ref, list or custom; a string because the files are imported as JSON
  kind: string;
  label: string;
  help?: string;
  required?: boolean;
  default?: unknown;
  max?: number;
  options?: { key: string; label: string }[];
  of?: Record<string, ConfigProp>;
  keyed?: boolean;
  editor?: string;
}

// How a card presents its job schema
export interface JobPresentation {
  outcomes: Record<string, Pick<JobSchemaOutcome, 'type' | 'label'>>;
  views?: JobSchema['views'];
  // an ns-icon name, drawn as the card header's bubble
  icon?: string;
}

export function jobSchema(definition: JobSchemaDefinition, presentation: JobPresentation): JobSchema {
  const definitionOutcomes = Object.keys(definition.outcomes).sort().join(',');
  if (Object.keys(presentation.outcomes).sort().join(',') !== definitionOutcomes) {
    throw new Error(`${definition.name} presents outcomes that differ from its definition's (${definitionOutcomes})`);
  }
  return {
    ...definition,
    ...presentation,
    // job schema files are checked against these types by the registry and the unit tests
    inputs: definition.inputs as JobSchemaInputs,
    outcomes: Object.fromEntries(
      Object.entries(definition.outcomes).map(([key, outcome]) => [
        key,
        { ...presentation.outcomes[key], inputs: outcome.inputs as JobSchemaInputs | undefined },
      ]),
    ),
  };
}
